/**
 * Screen-space overlay: drag-selection rectangle and the structure placement
 * ghost.
 *
 * These are 2D annotations tied to cursor position rather than world content,
 * so a plain canvas on top costs less than routing them through the instanced
 * renderer, and it can be redrawn only when something actually changes.
 */

import type { GameRenderer } from '../render/renderer.js';
import type { DragBox } from '../game/input.js';

export class Overlay {
  readonly canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  constructor(parent: HTMLElement) {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'overlay';
    this.ctx = this.canvas.getContext('2d')!;
    parent.appendChild(this.canvas);
    this.resize();
  }

  resize(): void {
    const dpr = Math.min(window.devicePixelRatio, 2);
    const w = this.canvas.clientWidth || window.innerWidth;
    const h = this.canvas.clientHeight || window.innerHeight;
    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(h * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  clear(): void {
    const dpr = Math.min(window.devicePixelRatio, 2);
    this.ctx.clearRect(0, 0, this.canvas.width / dpr, this.canvas.height / dpr);
  }

  /** Draws the classic dashed selection marquee. */
  drawSelectionBox(box: DragBox): void {
    const rect = this.canvas.getBoundingClientRect();
    const x0 = Math.min(box.x0, box.x1) - rect.left;
    const y0 = Math.min(box.y0, box.y1) - rect.top;
    const w = Math.abs(box.x1 - box.x0);
    const h = Math.abs(box.y1 - box.y0);

    const ctx = this.ctx;
    ctx.save();
    ctx.fillStyle = 'rgba(90, 200, 120, 0.12)';
    ctx.fillRect(x0, y0, w, h);
    ctx.strokeStyle = 'rgba(120, 240, 150, 0.9)';
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 4]);
    ctx.strokeRect(Math.round(x0) + 0.5, Math.round(y0) + 0.5, Math.round(w), Math.round(h));
    ctx.restore();
  }

  /**
   * Draws the footprint a structure would occupy, green when the spot is legal
   * and red when it is not, so the player gets the answer before committing.
   */
  drawPlacementGhost(
    renderer: GameRenderer,
    tileX: number,
    tileY: number,
    footprint: number,
    valid: boolean,
  ): void {
    const rect = this.canvas.getBoundingClientRect();
    const a = renderer.worldToScreen(tileX, tileY);
    const b = renderer.worldToScreen(tileX + footprint, tileY + footprint);

    const ctx = this.ctx;
    ctx.save();
    ctx.fillStyle = valid ? 'rgba(80, 220, 120, 0.28)' : 'rgba(230, 70, 60, 0.28)';
    ctx.strokeStyle = valid ? 'rgba(120, 255, 160, 0.95)' : 'rgba(255, 110, 100, 0.95)';
    ctx.lineWidth = 2;
    const x = a.x - rect.left;
    const y = a.y - rect.top;
    const w = b.x - a.x;
    const h = b.y - a.y;
    ctx.fillRect(x, y, w, h);
    ctx.strokeRect(x, y, w, h);

    // Grid lines inside the footprint, so multi-tile structures read clearly.
    ctx.strokeStyle = valid ? 'rgba(120, 255, 160, 0.4)' : 'rgba(255, 110, 100, 0.4)';
    ctx.lineWidth = 1;
    for (let i = 1; i < footprint; i++) {
      const gx = x + (w * i) / footprint;
      const gy = y + (h * i) / footprint;
      ctx.beginPath();
      ctx.moveTo(gx, y);
      ctx.lineTo(gx, y + h);
      ctx.moveTo(x, gy);
      ctx.lineTo(x + w, gy);
      ctx.stroke();
    }
    ctx.restore();
  }
}
